import { deleteData, getData, post, put } from 'services/api';
import { useQuery } from '@tanstack/react-query';

import { useToastMutation } from './useToastMutation';

export interface FanNote {
	id: number;
	note: string;
	created_at: string;
	updated_at: string;
}

export const usePartnerFanId = (userId: number | undefined): number | undefined => {
	const { data: fans = [] } = useQuery({
		queryKey: ['partner-fans'],
		queryFn: (): Promise<Array<{ partner_fan_id: number; user_id: number }>> =>
			// per_page=-1 fetches all fans; the default page size (15) would drop any
			// attendee past the first page and leave partner_fan_id unresolved.
			getData('/partners/fans', { params: { per_page: -1 } }).then((res) => res ?? []),
		enabled: !!userId,
		staleTime: 5 * 60 * 1000,
	});
	return fans.find((f) => f.user_id === userId)?.partner_fan_id;
};

export const useFanNotes = (partnerFanId: number | undefined) => {
	return useQuery({
		queryKey: ['fan-notes', partnerFanId],
		queryFn: (): Promise<FanNote[]> =>
			// Resumed (snake_case id/created_at) notes read. The legacy
			// /partners/fans-notes/:id endpoint stays raw for PunchupLive's dashboard.
			getData(`/partners/fans/notes/${partnerFanId}`).then((res) => res ?? []),
		enabled: !!partnerFanId,
	});
};

export const useCreateFanNote = (partnerFanId: number | undefined, options?: { onSuccess?: () => void }) => {
	const { mutate: createNote, isLoading } = useToastMutation<string>({
		mutationFn: (note) => post(`/partners/fans-notes/${partnerFanId}`, { note }),
		successMessage: 'Note added',
		errorMessage: 'Could not add note.',
		invalidateKeys: [['fan-notes', partnerFanId]],
		onSuccess: options?.onSuccess,
	});

	return { createNote, isLoading };
};

export const useUpdateFanNote = (partnerFanId: number | undefined, options?: { onSuccess?: () => void }) => {
	const { mutate: updateNote, isLoading } = useToastMutation<{ noteId: number; note: string }>({
		mutationFn: ({ noteId, note }) => put(`/partners/fans/notes/${noteId}`, { note }),
		successMessage: 'Note updated',
		errorMessage: 'Could not update note.',
		invalidateKeys: [['fan-notes', partnerFanId]],
		onSuccess: options?.onSuccess,
	});

	return { updateNote, isLoading };
};

export const useDeleteFanNote = (partnerFanId: number | undefined, options?: { onSuccess?: () => void }) => {
	const { mutate: deleteNote, isLoading } = useToastMutation<number>({
		mutationFn: (noteId) => deleteData(`/partners/fans-notes/${noteId}`),
		successMessage: 'Note deleted',
		errorMessage: 'Could not delete note.',
		invalidateKeys: [['fan-notes', partnerFanId]],
		onSuccess: options?.onSuccess,
	});

	return { deleteNote, isLoading };
};
